import {
  browserVersion,
  isChrome,
  isIOS,
  isIPad13,
  isIPhone13,
  isMacOs,
  isMobileSafari,
  isSafari,
  isSamsungBrowser,
} from "react-device-detect"

export enum Platforms {
  student = "Student",
  studentJobs = "Student Jobs",
  centerAdmin = "Centre Admin",
  professor = "Professor",
  serviceAdmin = "Service Admin",
  iqa = "Internal Quality Assessor",
  svp = "School Verification Portal",
  school = "School",
  apiDocs = "API Docs",
  olympiadPartner = "Olympiad Partner",
  applicationFormAdmin = "Application Form Admin",
  recruiter = "Recruitment Portal",
}

export const USER_TYPE = Platforms.student
export const RTL_LANGUAGE_CODES = ["ar"]

export const DEFAULT_LANGUAGE_CODE = "en"

export const PAGE_SIZE = 10

export const BROWSER_STORAGE_KEYS = {
  auth: "suraasaAuth",
  language: "suraasaLanguage",
  suraasaMockOlympiadTourCompleted: "suraasaMockOlympiadTourCompleted",
  ipAddress: "ipAddress",
  cleverOAuthState: "cleverOAuthState",

  // TODO: Cleanup this key from preferences
  // suraasaOnboardingCompleted: "suraasaOnboardingCompleted",
  pgctlApplicationFormBannerDismissed: "suraasaApplicationFormBannerDismissed",
  medApplicationFormBannerDismissed: "medApplicationFormBannerDismissed",
  tempDeviceId: "tempDeviceId",
  dismissITOinkReportsBanner: "dismissITOLinkReportsBanner",
  cameraPermissionsAllowedForITO: "itoCamPermissionAllowed",
  dismissedITOCongratulationsDialog: "dismissedITOCongratulationsDialog",
  reorderOnboarding: "suraasaSubjectsReorderOnboarding",
  hasSeenITOBadgeAnimation: "hasSeenITOBadgeAnimation",
  replyDiscussionSheetScrollPosition: "rdsScrollPosition",
  ryanSignupOnboardingCompleted: "ryanSignupOnboardingCompleted",
  courseExpiryWarning: "courseExpiryWarning",
  assignmentDueDateWarning: "assignmentDueDateWarning",
  assignmentExpiredWarning: "assignmentExpiredWarning",

  // Sign up onboarding
  signupOnboardingCompleted: "signupOnboardingCompleted",
  signupOnboardingPromptMode: "signupOnboardingPromptMode",
  pgctlEnrollmentAgreementAccepted: "pgctlEnrollmentAgreementAccepted",
  pgctlOrientationVideoCompleted: "pgctlOrientationVideoCompleted",
  tdc_dsbAgreementSeen: "tdc_dsbAgreementSeen",
  hasDismissedOptionalSignupOnboardingPopup:
    "hasDismissedOptionalSignupOnboardingPopup",

  // Onboarding keys
  dashboardTourCompleted: "dashboardTourCompleted",
  profileTourCompleted: "profileTourCompleted",

  pgctlWelcomeModalViewedAt: "pgctlWelcomeModalViewedAt",
  pgctlOfferLetterViewedAt: "pgctlOfferLetterViewedAt",

  // SP Stands for Suraasa Plus
  hasClaimedTeacherMonthSP: "hasClaimedTeacherMonthSP",

  hasDismissedAIGeneratedSubmissionWarning:
    "hasDismissedAIGeneratedSubmissionWarning",

  // Synced with app
  suraasaAIToolsOnboardingCompleted: "suraasaAIToolsOnboardingCompleted",
  // TODO: Cleanup this key from preferences
  uploadResumeNudgeDismissedTime: "uploadResumeNudgeDismissedTime",
  videoLanguagePreference: "videoLanguagePreference",
  showVideoSubtitles: "showVideoSubtitles",
  // resumeFillerStage: "resumeFillerStage",

  // Session UTM Params
  sessionUtmParams: "sessionUtmParams",

  // PostHog person properties already sent this session
  postHogPersonProperties: "postHogPersonProperties",

  // Maintenance
  maintenance: "suraasaMaintenance",

  // Key for AI tools card at the home page
  lastUsedAiTool: "lastUsedAiTool",

  // Key for saving course video's timestamps and speed
  learningVideoProgress: "learningVideoProgress",
  learningVideoPreferredPlaybackSpeed: "learningVideoPreferredPlaybackSpeed",

  // Key for showing suraasa plus feature overview sheet
  suraasaPlusFeatureOverview: "suraasaPlusFeatureOverviewViewed",
  userOrganizations: "userOrganizations",

  // Suraasa Plus New Year onboarding keys
  spnyPlatformIntro: "spnyPlatformIntro",

  spnyMasterclass: "spnyMasterclass",
  spnyAiTools: "spnyAiTools",
  spnyImpactCourse: "spnyImpactCourse",
  spnyJobs: "spnyJobs",
  spnyCpatDashboardTour: "spnyCpatDashboardTour",

  // The key and value names are different because we're retaining the old value for users who have already seen the tour, but we want to use a more descriptive key name going forward
  // We can update the value when backend migrates all the old values to the new key.
  cpatOnboardingQuestionnaireStatus: "spnyQuestionnaire",
  cpatTour: "cpatTour",
  cpatReportViewed: "cpatReportViewed",
} as const

export const PLATFORM_SETTINGS_SLUGS = {
  showChatbotV1: "sales-chatbot-v1",
} as const

export enum UpdateProfileEnum {
  PENDING = "pending",
  SUBJECTS = "subjects",
  LOCATIONS = "locations",
}

export enum SERVICE_PREFIX {
  ascella = "ascella",
  doppler = "doppler",
  mercury = "mercury",
  pollux = "pollux",
  castor = "castor",
  libra = "libra",
  gravity = "gravity",
  cygnus = "cygnus",
  hermes = "hermes",
  solis = "solis",
  // helios = "helios",
  norma = "norma",
  perseus = "perseus",
  photon = "photon",
  sirius = "sirius",
  ophelia = "ophelia",
  polaris = "polaris",
  kepler = "kepler",
  phoenix = "phoenix",
  iris = "iris",
  axion = "axion",
  celestial = "celestial",
  nexus = "nexus",
  zenith = "zenith",
  eris = "eris",
}

export const isRTL = RTL_LANGUAGE_CODES.includes(
  localStorage.getItem(BROWSER_STORAGE_KEYS.language) || ""
)

export const isAppleDevice =
  isIOS || isMacOs || isIPad13 || isIPhone13 || isSafari || isMobileSafari

const checkVersion = () => {
  const MIN_CHROME_VERSION = 100
  const MIN_SAFARI_VERSION = 16
  const MIN_SAMSUNG_BROWSER_VERSION = 19 // Based on Chromium 102. https://en.wikipedia.org/wiki/Samsung_Internet

  if (isSamsungBrowser) {
    const version = parseInt(browserVersion)
    return version >= MIN_SAMSUNG_BROWSER_VERSION
  }

  if (isChrome) {
    const version = parseInt(browserVersion)
    return version >= MIN_CHROME_VERSION
  }

  if (isSafari || isMobileSafari) {
    const version = parseInt(browserVersion)
    return version >= MIN_SAFARI_VERSION
  }
}

export const browserIsSupportedForProctoring = (() => {
  const blockedAgents = [
    "Perplexity",
    "Atlas",
    "GPTBot",
    "ChatGPT",
    "Omgili",
    "Claude",
    "AI",
    "Scraper",
  ]

  const ua = navigator.userAgent || ""
  if (
    blockedAgents.some(agent => ua.toLowerCase().includes(agent.toLowerCase()))
  ) {
    return "unsupported_vendor"
  }

  const vendorSupported =
    isChrome || isSafari || isMobileSafari || isSamsungBrowser
  const versionSupported = checkVersion()

  if (!vendorSupported) {
    return "unsupported_vendor"
  }

  if (!versionSupported) {
    return "unsupported_version"
  }

  return "supported"
})()

export const cpatQuestions = 50
export const cpatDurationInMinutes = 60
export const cpatPattern = "MCQ"
export const cpatType = "cpat_assessment"

export const learningItemSlugs = {
  pgctl: "professional-graduate-certificate-in-teaching-learning",
  med: "masters-in-education",
  impactCourse:
    "instructional-mastery-pedagogy-advancement-for-classroom-teaching",
  boostingEmployabilityV2: "boosting-employability-v2",
}

export enum PgCTLProductSlugsEnum {
  starter = "pgctl-starter",
  professional = "pgctl-professional",
  executive = "pgctl-executive",
}

export const learningItemName = {
  pgctl: "Professional Graduate Certificate in Teaching & Learning",
  med: "Masters' in Education",
}

export const applicationFormSlugs = {
  pgctl: "professional-graduate-certificate-in-teaching-learning",
  med: "masters-in-education",
} as const

export const importantSearchParams = {
  platformOverride: "po",
  dialog: "dialog",
}

export const MAXIMUM_SCORE = {
  assignment: 3,
  assessment: 100,
}

export const IS_LOCALHOST = import.meta.env.DEV
export const IS_DEV_ENVIRONMENT =
  import.meta.env.VITE_DEPLOYMENT_ENVIRONMENT === "dev"
export const IS_PROD_ENVIRONMENT =
  import.meta.env.VITE_DEPLOYMENT_ENVIRONMENT === "prod"

export const signupSources = {
  talkToAMentor: "talk-to-a-mentor",
  gsgCpat: "gsg_cpat",
  direct: "direct",
}

export const CPAT_PRODUCT_SLUG = "pedagogy-assessment-of-teachers"

export const profileStrengthActionKeyParam = "profileStrengthActionKey"

export const NotificationURLParam = "notification-action"

export const SURAASA_PLUS_START_FREE_TRIAL_EVENT_NAME =
  "suraasa_plus.claim_free_trial"
export const SURAASA_PLUS_SUBSCRIPTION_PRODUCT_SLUG =
  "suraasa-plus-subscription"
export const SPNY_26_SLUG = "suraasa-plus-new-year-2026"

export const PAYMENT_UNDER_PROCESS_PAGE_PORTAL =
  "payment-under-process-page-portal"

export const GSG_CPAT_SLUG_PATTERN = "global-schools-group"
